import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogService, WarehouseTask } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-warehouse-tasks',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, ButtonModule, TableModule, AinCanAccessDirective, FormsModule, InputTextModule],
  template: `
    <ain-page [title]="t('catalog.outbound.tasksTitle')" [description]="t('catalog.outbound.tasksDescription')">
      <ain-inventory-page-hint pageKey="warehouseTasks" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>
      @if (loading()) { <ain-state type="loading" [title]="t('catalog.loading')" /> }
      @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      }
      @else {
        <p-table [value]="tasks()">
          <ng-template pTemplate="header">
            <tr><th>{{ t('columns.type') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('catalog.inventory.quantity') }}</th><th></th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.taskType }}</td><td>{{ row.status }}</td><td>{{ row.quantity }}</td>
              <td>
                @if (row.status === 'Pending' && row.taskType === 'PutAway') {
                  <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.inbound.completePutAway')" (click)="completePutAway(row)"></button>
                }
                @if (row.status === 'Pending' && row.taskType === 'Pick') {
                  <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.outbound.completePick')" (click)="completePick(row)"></button>
                }
                @if (row.status === 'Pending' && row.taskType === 'Pack') {
                  <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.operator.completePack')" (click)="completePack(row)"></button>
                }
                @if (row.status === 'Pending' && row.taskType === 'Ship') {
                  <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.operator.completeShip')" (click)="completeShip(row)"></button>
                }
                @if (row.status === 'Completed' && row.taskType === 'Pack') {
                  <button pButton type="button" size="small" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.operator.createShip')" (click)="createShip(row)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
        @if (shipTaskId()) {
          <section class="ship-panel">
            <h4>{{ t('catalog.integrations.confirmShipTitle') }}</h4>
            <div class="ship-grid">
              <label><span>{{ t('catalog.integrations.carrierCode') }}</span><input pInputText [(ngModel)]="shipForm.carrierCode" /></label>
              <label><span>{{ t('catalog.integrations.carrier') }}</span><input pInputText [(ngModel)]="shipForm.carrierName" /></label>
              <label><span>{{ t('catalog.integrations.trackingNumber') }}</span><input pInputText [(ngModel)]="shipForm.trackingNumber" /></label>
              <label><span>{{ t('catalog.integrations.vehicleRef') }}</span><input pInputText [(ngModel)]="shipForm.vehicleRef" /></label>
              <label><span>{{ t('catalog.integrations.driverRef') }}</span><input pInputText [(ngModel)]="shipForm.driverRef" /></label>
            </div>
            <button pButton type="button" [label]="t('catalog.operator.completeShip')" (click)="submitShipConfirm()"></button>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .ship-panel { margin-top: 16px; padding: 14px; border: 1px solid #e2e8f0; border-radius: 12px; display: grid; gap: 10px; }
    .ship-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
    .ship-grid label { display: grid; gap: 4px; font-size: 13px; }
  `]
})
export class InventoryWarehouseTasksComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly tasks = signal<WarehouseTask[]>([]);
  readonly shipTaskId = signal<string | null>(null);
  shipForm = { carrierCode: '', carrierName: '', trackingNumber: '', vehicleRef: '', driverRef: '' };
  private taskIdFilter = '';
  t = (k: string) => this.localization.translate(k);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload()
    );
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.taskIdFilter = params.get('taskId') ?? '';
      this.reload();
    });
  }

  completePutAway(task: WarehouseTask): void {
    this.service.completePutAwayTask(task.id, `put-${task.id}-${Date.now()}`).subscribe({ next: () => this.reload() });
  }

  completePick(task: WarehouseTask): void {
    this.service.completePickTask(task.id, `pick-${task.id}-${Date.now()}`).subscribe({ next: () => this.reload() });
  }

  completePack(task: WarehouseTask): void {
    this.service.completePackTask(task.id).subscribe({ next: () => this.reload() });
  }

  completeShip(task: WarehouseTask): void {
    this.shipTaskId.set(task.id);
    this.shipForm = { carrierCode: '', carrierName: '', trackingNumber: '', vehicleRef: '', driverRef: '' };
  }

  submitShipConfirm(): void {
    const taskId = this.shipTaskId();
    if (!taskId) return;
    this.service.completeShipTask(taskId, {
      idempotencyKey: `ship-${taskId}-${Date.now()}`,
      carrierCode: this.shipForm.carrierCode || null,
      carrierName: this.shipForm.carrierName || null,
      trackingNumber: this.shipForm.trackingNumber || null,
      vehicleRef: this.shipForm.vehicleRef || null,
      driverRef: this.shipForm.driverRef || null
    }).subscribe({
      next: () => { this.shipTaskId.set(null); this.reload(); }
    });
  }

  createShip(task: WarehouseTask): void {
    this.service.createShipTaskFromPack(task.id).subscribe({ next: () => this.reload() });
  }

  reload(): void {
    if (this.taskIdFilter) {
      this.service.listWarehouseTasks(undefined, undefined, this.taskIdFilter).subscribe({
        next: tasks => {
          this.tasks.set(tasks);
          this.permissionDenied.set(false);
          this.loading.set(false);
        },
        error: err => {
          this.tasks.set([]);
          this.permissionDenied.set(err?.status === 403);
          this.loading.set(false);
        }
      });
      return;
    }

    this.service.listWarehouseTasks('Pending').subscribe({
      next: pending => {
        this.service.listWarehouseTasks('Completed', 'Pack').subscribe({
          next: packs => {
            this.tasks.set([...pending, ...packs]);
            this.permissionDenied.set(false);
            this.loading.set(false);
          },
          error: err => { this.permissionDenied.set(err?.status === 403); this.loading.set(false); }
        });
      },
      error: err => { this.permissionDenied.set(err?.status === 403); this.loading.set(false); }
    });
  }
}
